import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut,
  Search,
  Download,
  Trash2,
  X,
  Filter,
  RefreshCw,
  ArrowUpRight,
  TrendingUp,
  Users,
  Mail,
  Send,
} from "lucide-react";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "hwi_admin_token";

const STATUSES = ["new", "contacted", "won", "lost"];

const STATUS_STYLES = {
  new: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
  contacted: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  won: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  lost: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
};

// ─── helpers ────────────────────────────────────────────────────────────
const fmtDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const csvEscape = (v) => {
  if (v === null || v === undefined) return "";
  const s = Array.isArray(v) ? v.join("; ") : String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const downloadCsv = (filename, rows, columns) => {
  const header = columns.map((c) => c.label).join(",");
  const body = rows
    .map((r) => columns.map((c) => csvEscape(c.get(r))).join(","))
    .join("\n");
  const csv = `${header}\n${body}\n`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// ─── login form ─────────────────────────────────────────────────────────
function LoginGate({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/admin-login`, {
        password,
      });
      localStorage.setItem(TOKEN_KEY, data.token);
      onSuccess(data.token);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-zinc-900/60 border border-zinc-800 rounded-2xl p-8 backdrop-blur"
        data-testid="admin-login-form"
      >
        <div className="mb-6">
          <div className="text-xs tracking-[0.2em] text-cyan-400 mb-2">
            ADMIN ACCESS
          </div>
          <h1 className="text-3xl font-bold text-white">Sign in.</h1>
          <p className="text-sm text-zinc-500 mt-2">
            Private dashboard for lead submissions.
          </p>
        </div>

        <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
          Password
        </label>
        <input
          type="password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-zinc-950 border border-zinc-800 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 rounded-lg px-4 py-3 text-white outline-none transition"
          data-testid="admin-password-input"
        />
        {error && (
          <div
            className="text-sm text-red-400 mt-3"
            data-testid="admin-login-error"
          >
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={loading || !password}
          className="mt-6 w-full bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 text-black font-semibold py-3 rounded-lg transition"
          data-testid="admin-login-submit"
        >
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

// ─── row detail modal ──────────────────────────────────────────────────
function RowModal({ row, onClose }) {
  if (!row) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
      onClick={onClose}
      data-testid="admin-row-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-zinc-900 border border-zinc-800 rounded-2xl p-8"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-xs tracking-[0.2em] text-cyan-400 mb-1">
              LEAD DETAIL
            </div>
            <h2 className="text-2xl font-bold text-white">
              {row.name || row.email}
            </h2>
            {row.company && (
              <div className="text-zinc-400 mt-1">{row.company}</div>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-white p-2"
            aria-label="Close"
            data-testid="admin-row-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
          {Object.entries(row).map(([k, v]) => {
            if (k === "id" || k === "_id") return null;
            return (
              <div key={k}>
                <dt className="text-xs uppercase tracking-widest text-zinc-500 mb-1">
                  {k.replace(/_/g, " ")}
                </dt>
                <dd className="text-sm text-zinc-200 break-words">
                  {Array.isArray(v)
                    ? v.join(", ")
                    : k === "submitted_at"
                      ? fmtDate(v)
                      : v?.toString() || "-"}
                </dd>
              </div>
            );
          })}
        </dl>
      </div>
    </div>
  );
}

// ─── filters bar ───────────────────────────────────────────────────────
function FiltersBar({
  search,
  setSearch,
  range,
  setRange,
  statusFilter,
  setStatusFilter,
  onExport,
  onRefresh,
  counts,
}) {
  return (
    <div className="flex flex-col md:flex-row gap-3 md:items-center mb-4">
      <div className="relative flex-1">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, email, company, message…"
          className="w-full bg-zinc-900 border border-zinc-800 focus:border-cyan-500/60 rounded-lg pl-9 pr-3 py-2 text-sm text-white outline-none"
          data-testid="admin-search-input"
        />
      </div>

      <select
        value={range}
        onChange={(e) => setRange(e.target.value)}
        className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white outline-none"
        data-testid="admin-date-range"
      >
        <option value="7">Last 7 days</option>
        <option value="30">Last 30 days</option>
        <option value="90">Last 90 days</option>
        <option value="all">All time</option>
      </select>

      <select
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white outline-none"
        data-testid="admin-status-filter"
      >
        <option value="all">All statuses</option>
        {STATUSES.map((s) => (
          <option key={s} value={s}>
            {s[0].toUpperCase() + s.slice(1)}
          </option>
        ))}
      </select>

      <div className="text-xs text-zinc-500 flex items-center gap-2 pl-1">
        <Filter className="w-3.5 h-3.5" />
        {counts.filtered} / {counts.total}
      </div>

      <button
        onClick={onRefresh}
        className="flex items-center gap-1.5 bg-zinc-900 border border-zinc-800 hover:border-zinc-600 rounded-lg px-3 py-2 text-sm text-zinc-300"
        data-testid="admin-refresh"
      >
        <RefreshCw className="w-3.5 h-3.5" /> Refresh
      </button>

      <button
        onClick={onExport}
        className="flex items-center gap-1.5 bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/20 text-cyan-300 rounded-lg px-3 py-2 text-sm"
        data-testid="admin-export-csv"
      >
        <Download className="w-3.5 h-3.5" /> Export CSV
      </button>
    </div>
  );
}

// ─── shared row-filter hook ────────────────────────────────────────────
function useFilteredRows(rows, search, range, statusFilter) {
  return useMemo(() => {
    const now = Date.now();
    const cutoff =
      range === "all" ? 0 : now - Number(range) * 24 * 60 * 60 * 1000;
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      const submittedMs = r.submitted_at
        ? new Date(r.submitted_at).getTime()
        : 0;
      if (submittedMs && submittedMs < cutoff) return false;
      if (!term) return true;
      const haystack = JSON.stringify(r).toLowerCase();
      return haystack.includes(term);
    });
  }, [rows, search, range, statusFilter]);
}

// ─── positioning reviews tab ───────────────────────────────────────────
function PositioningTab({ token, onOpenRow }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [range, setRange] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/positioning-reviews`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(data);
    } catch (e) {
      toast.error("Failed to load positioning reviews");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useFilteredRows(rows, search, range, statusFilter);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/positioning-reviews/${id}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRows((prev) =>
        prev.map((r) => (r.id === id ? { ...r, status } : r))
      );
    } catch {
      toast.error("Update failed");
    }
  };

  const deleteRow = async (id) => {
    if (!window.confirm("Delete this lead? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/positioning-reviews/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows((prev) => prev.filter((r) => r.id !== id));
      toast.success("Lead deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const exportCsv = () => {
    downloadCsv("positioning-reviews.csv", filtered, [
      { label: "Submitted", get: (r) => r.submitted_at },
      { label: "Name", get: (r) => r.name },
      { label: "Email", get: (r) => r.email },
      { label: "Company", get: (r) => r.company },
      { label: "Role", get: (r) => r.role },
      { label: "Phone", get: (r) => r.phone },
      { label: "Pillar", get: (r) => r.pillar },
      { label: "Market Position", get: (r) => r.market_position },
      { label: "Roadblock", get: (r) => r.roadblock },
      { label: "Notes", get: (r) => r.notes },
      { label: "Status", get: (r) => r.status },
    ]);
  };

  return (
    <div>
      <FiltersBar
        search={search}
        setSearch={setSearch}
        range={range}
        setRange={setRange}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        onExport={exportCsv}
        onRefresh={load}
        counts={{ filtered: filtered.length, total: rows.length }}
      />
      <div className="border border-zinc-800 rounded-lg overflow-hidden">
        <table
          className="w-full text-sm"
          data-testid="admin-positioning-table"
        >
          <thead className="bg-zinc-900/60 text-xs uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Submitted</th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Company</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Pillar</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {loading && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-zinc-500">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-zinc-500">
                  No positioning reviews match your filters.
                </td>
              </tr>
            )}
            {!loading &&
              filtered.map((r) => (
                <tr
                  key={r.id}
                  className="hover:bg-zinc-900/40 cursor-pointer"
                  onClick={() => onOpenRow(r)}
                  data-testid={`admin-positioning-row-${r.id}`}
                >
                  <td className="px-4 py-3 text-zinc-400 whitespace-nowrap">
                    {fmtDate(r.submitted_at)}
                  </td>
                  <td className="px-4 py-3 text-white">{r.name}</td>
                  <td className="px-4 py-3 text-zinc-300">{r.company || "-"}</td>
                  <td className="px-4 py-3 text-zinc-300">{r.email}</td>
                  <td className="px-4 py-3 text-zinc-400 max-w-[200px] truncate">
                    {(r.pillar || []).join(", ") || "-"}
                  </td>
                  <td
                    className="px-4 py-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <select
                      value={r.status || "new"}
                      onChange={(e) => updateStatus(r.id, e.target.value)}
                      className={`text-xs uppercase tracking-widest rounded-full px-2.5 py-1 border ${STATUS_STYLES[r.status || "new"]} outline-none`}
                      data-testid={`admin-status-select-${r.id}`}
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s} className="bg-zinc-900">
                          {s.toUpperCase()}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td
                    className="px-4 py-3 text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => deleteRow(r.id)}
                      className="text-zinc-500 hover:text-red-400 p-1.5"
                      aria-label="Delete"
                      data-testid={`admin-delete-${r.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── contacts tab ──────────────────────────────────────────────────────
function ContactsTab({ token, onOpenRow }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [range, setRange] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/contacts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(data);
    } catch {
      toast.error("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useFilteredRows(rows, search, range, statusFilter);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/contacts/${id}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    } catch {
      toast.error("Update failed");
    }
  };

  const deleteRow = async (id) => {
    if (!window.confirm("Delete this message? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows((prev) => prev.filter((r) => r.id !== id));
      toast.success("Contact deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const exportCsv = () => {
    downloadCsv("contacts.csv", filtered, [
      { label: "Submitted", get: (r) => r.submitted_at },
      { label: "Name", get: (r) => r.name },
      { label: "Email", get: (r) => r.email },
      { label: "Company", get: (r) => r.company },
      { label: "Message", get: (r) => r.message },
      { label: "Status", get: (r) => r.status },
    ]);
  };

  return (
    <div>
      <FiltersBar
        search={search}
        setSearch={setSearch}
        range={range}
        setRange={setRange}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        onExport={exportCsv}
        onRefresh={load}
        counts={{ filtered: filtered.length, total: rows.length }}
      />
      <div className="border border-zinc-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-contacts-table">
          <thead className="bg-zinc-900/60 text-xs uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Submitted</th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Company</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Message</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {loading && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-zinc-500">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-zinc-500">
                  No contact messages match your filters.
                </td>
              </tr>
            )}
            {!loading &&
              filtered.map((r) => (
                <tr
                  key={r.id}
                  className="hover:bg-zinc-900/40 cursor-pointer"
                  onClick={() => onOpenRow(r)}
                  data-testid={`admin-contact-row-${r.id}`}
                >
                  <td className="px-4 py-3 text-zinc-400 whitespace-nowrap">
                    {fmtDate(r.submitted_at)}
                  </td>
                  <td className="px-4 py-3 text-white">{r.name}</td>
                  <td className="px-4 py-3 text-zinc-300">{r.company || "-"}</td>
                  <td className="px-4 py-3 text-zinc-300">{r.email}</td>
                  <td className="px-4 py-3 text-zinc-400 max-w-[280px] truncate">
                    {r.message}
                  </td>
                  <td
                    className="px-4 py-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <select
                      value={r.status || "new"}
                      onChange={(e) => updateStatus(r.id, e.target.value)}
                      className={`text-xs uppercase tracking-widest rounded-full px-2.5 py-1 border ${STATUS_STYLES[r.status || "new"]} outline-none`}
                      data-testid={`admin-contact-status-select-${r.id}`}
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s} className="bg-zinc-900">
                          {s.toUpperCase()}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td
                    className="px-4 py-3 text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => deleteRow(r.id)}
                      className="text-zinc-500 hover:text-red-400 p-1.5"
                      aria-label="Delete"
                      data-testid={`admin-contact-delete-${r.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── newsletter tab ────────────────────────────────────────────────────
function NewsletterTab({ token, onOpenRow }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [range, setRange] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/newsletter`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(data);
    } catch {
      toast.error("Failed to load subscribers");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const now = Date.now();
    const cutoff =
      range === "all" ? 0 : now - Number(range) * 24 * 60 * 60 * 1000;
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      const subMs = r.subscribed_at ? new Date(r.subscribed_at).getTime() : 0;
      if (subMs && subMs < cutoff) return false;
      if (!term) return true;
      return JSON.stringify(r).toLowerCase().includes(term);
    });
  }, [rows, search, range]);

  const deleteRow = async (id) => {
    if (!window.confirm("Remove this subscriber? This cannot be undone."))
      return;
    try {
      await axios.delete(`${API}/admin/newsletter/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows((prev) => prev.filter((r) => r.id !== id));
      toast.success("Subscriber removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  const exportCsv = () => {
    downloadCsv("newsletter-subscribers.csv", filtered, [
      { label: "Subscribed", get: (r) => r.subscribed_at },
      { label: "Email", get: (r) => r.email },
      { label: "Source", get: (r) => r.source },
    ]);
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row gap-3 md:items-center mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search email or source…"
            className="w-full bg-zinc-900 border border-zinc-800 focus:border-cyan-500/60 rounded-lg pl-9 pr-3 py-2 text-sm text-white outline-none"
            data-testid="admin-newsletter-search"
          />
        </div>
        <select
          value={range}
          onChange={(e) => setRange(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white outline-none"
          data-testid="admin-newsletter-range"
        >
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="all">All time</option>
        </select>
        <div className="text-xs text-zinc-500 flex items-center gap-2 pl-1">
          <Filter className="w-3.5 h-3.5" />
          {filtered.length} / {rows.length}
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1.5 bg-zinc-900 border border-zinc-800 hover:border-zinc-600 rounded-lg px-3 py-2 text-sm text-zinc-300"
          data-testid="admin-newsletter-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
        <button
          onClick={exportCsv}
          className="flex items-center gap-1.5 bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/20 text-cyan-300 rounded-lg px-3 py-2 text-sm"
          data-testid="admin-newsletter-export"
        >
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>
      <div className="border border-zinc-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-newsletter-table">
          <thead className="bg-zinc-900/60 text-xs uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Subscribed</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Source</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {loading && (
              <tr>
                <td colSpan={4} className="px-4 py-10 text-center text-zinc-500">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-10 text-center text-zinc-500">
                  No subscribers match your filters.
                </td>
              </tr>
            )}
            {!loading &&
              filtered.map((r) => (
                <tr
                  key={r.id}
                  className="hover:bg-zinc-900/40 cursor-pointer"
                  onClick={() => onOpenRow(r)}
                  data-testid={`admin-newsletter-row-${r.id}`}
                >
                  <td className="px-4 py-3 text-zinc-400 whitespace-nowrap">
                    {fmtDate(r.subscribed_at)}
                  </td>
                  <td className="px-4 py-3 text-white">{r.email}</td>
                  <td className="px-4 py-3 text-zinc-400">
                    {r.source || "-"}
                  </td>
                  <td
                    className="px-4 py-3 text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => deleteRow(r.id)}
                      className="text-zinc-500 hover:text-red-400 p-1.5"
                      aria-label="Remove subscriber"
                      data-testid={`admin-newsletter-delete-${r.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── analytics tab ─────────────────────────────────────────────────────
function AnalyticsTab({ token }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSummary(data);
    } catch {
      toast.error("Failed to load summary");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const cards = [
    {
      key: "positioning_reviews",
      label: "Positioning Reviews",
      icon: Users,
      accent: "text-cyan-400",
    },
    {
      key: "contacts",
      label: "Contact Submissions",
      icon: Mail,
      accent: "text-cyan-400",
    },
    {
      key: "newsletter",
      label: "Newsletter Subscribers",
      icon: Send,
      accent: "text-cyan-400",
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">
            At a glance
          </div>
          <h2 className="text-2xl font-bold">Site analytics</h2>
          {summary && (
            <p className="text-xs text-zinc-500 mt-1">
              Generated {new Date(summary.generated_at).toLocaleString("en-GB")}
            </p>
          )}
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1.5 bg-zinc-900 border border-zinc-800 hover:border-zinc-600 rounded-lg px-3 py-2 text-sm text-zinc-300"
          data-testid="admin-analytics-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-500">Loading…</div>
      ) : summary ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {cards.map(({ key, label, icon: Icon, accent }) => {
            const c = summary[key] || { total: 0, week: 0, month: 0 };
            return (
              <div
                key={key}
                className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6"
                data-testid={`analytics-card-${key}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="text-xs uppercase tracking-widest text-zinc-500">
                    {label}
                  </div>
                  <Icon className={`w-4 h-4 ${accent}`} />
                </div>
                <div className="text-4xl font-bold tracking-tight">
                  {c.total.toLocaleString("en-GB")}
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  All-time submissions
                </div>
                <div className="mt-5 grid grid-cols-2 gap-3 border-t border-zinc-800 pt-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                      Last 7 days
                    </div>
                    <div className="text-lg font-semibold mt-0.5">
                      {c.week}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                      Last 30 days
                    </div>
                    <div className="text-lg font-semibold mt-0.5">
                      {c.month}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {/* PostHog external link */}
      <div className="mt-8 border border-zinc-800 rounded-2xl p-6 bg-zinc-900/60">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-cyan-500/10 rounded-lg">
            <TrendingUp className="w-5 h-5 text-cyan-400" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-cyan-400">
              Deep analytics
            </div>
            <div className="text-lg font-semibold mt-1">
              PostHog dashboard
            </div>
            <p className="text-sm text-zinc-400 mt-2 leading-relaxed max-w-2xl">
              Session recordings, funnel drop-off, page-by-page conversion,
              referrer breakdown and heatmaps live in your PostHog workspace.
              These numbers here are our own DB counts, PostHog handles
              everything behavioural.
            </p>
            <a
              href="https://us.posthog.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="analytics-open-posthog"
              className="mt-4 inline-flex items-center gap-1.5 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold px-4 py-2 rounded-lg text-sm transition"
            >
              Open PostHog <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── shell ─────────────────────────────────────────────────────────────
export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [checking, setChecking] = useState(!!token);
  const [tab, setTab] = useState("analytics");
  const [openRow, setOpenRow] = useState(null);

  // Validate stored token on mount
  useEffect(() => {
    if (!token) {
      setChecking(false);
      return;
    }
    axios
      .get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => setChecking(false))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setChecking(false);
      });
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (checking) {
    return (
      <div className="admin-dark min-h-screen bg-zinc-950 flex items-center justify-center text-zinc-500">
        Loading…
      </div>
    );
  }

  if (!token)
    return (
      <div className="admin-dark">
        <LoginGate onSuccess={setToken} />
      </div>
    );

  return (
    <div className="admin-dark min-h-screen bg-zinc-950 text-white">
      <Seo title="Admin - Leads" noindex />
      {/* Top bar */}
      <header className="border-b border-zinc-800 bg-zinc-950/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.25em] text-cyan-400">
              HIGHWAYSINDUSTRY.COM · ADMIN
            </div>
            <h1 className="text-xl font-bold">Leads dashboard</h1>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white border border-zinc-800 hover:border-zinc-600 rounded-lg px-3 py-1.5"
            data-testid="admin-logout"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-6 flex gap-1">
          {[
            { id: "analytics", label: "Analytics" },
            { id: "positioning", label: "Positioning Reviews" },
            { id: "contacts", label: "Contact Submissions" },
            { id: "newsletter", label: "Newsletter" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 -mb-px text-sm border-b-2 transition ${
                tab === t.id
                  ? "border-cyan-400 text-white"
                  : "border-transparent text-zinc-500 hover:text-zinc-300"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {tab === "analytics" && <AnalyticsTab token={token} />}
        {tab === "positioning" && (
          <PositioningTab token={token} onOpenRow={setOpenRow} />
        )}
        {tab === "contacts" && (
          <ContactsTab token={token} onOpenRow={setOpenRow} />
        )}
        {tab === "newsletter" && (
          <NewsletterTab token={token} onOpenRow={setOpenRow} />
        )}
      </main>

      <RowModal row={openRow} onClose={() => setOpenRow(null)} />
    </div>
  );
}
